export const HELP_QUESTION_MAX_LENGTH = 500;
export const HELP_ROUTE_MAX_LENGTH = 240;
export const HELP_HISTORY_MAX_MESSAGES = 4;
export const HELP_HISTORY_MESSAGE_MAX_LENGTH = 600;
export const HELP_RETRIEVAL_LIMIT = 4;
export const HELP_MAX_OUTPUT_TOKENS = 400;
export const HELP_CONTENT_VERSION = '2026-08-31.1';
export const HELP_FEEDBACK_NOTE_MAX_LENGTH = 300;
export const HELP_FEEDBACK_RETENTION_DAYS = 90;
export const HELP_OPERATIONS_MAX_ENVIRONMENTS = 2;
export const HELP_OPERATIONS_MAX_PROCESSES = 5;

export interface HelpConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface AskHelpRequest {
  question: string;
  currentRoute?: string;
  history?: HelpConversationMessage[];
  clientRevision?: string;
  operationsContext?: HelpOperationsContext;
}

export type HelpOperationsHealth = 'HEALTHY' | 'ATTENTION' | 'DEGRADED' | 'UNAVAILABLE';

export interface HelpOperationsProcess {
  name: string;
  invocations: number;
  errors: number;
  throttles: number;
  averageDurationMs: number;
}

export interface HelpOperationsEnvironment {
  stage: string;
  health: HelpOperationsHealth;
  metricsWindowHours: number;
  apiRequests: number;
  serviceErrors: number;
  lambdaThrottles: number;
  dynamodbThrottles: number;
  queueVisibleMessages: number;
  queueOldestMessageSeconds: number;
  deadLetterMessages: number;
  activeAlarmCount: number;
  unresolvedEbayExceptions: number;
  ebayInventoryUpdatedAt?: string;
  ebayOrdersUpdatedAt?: string;
  monthToDateUsd?: number;
  forecastMonthEndUsd?: number;
  costStale?: boolean;
  topProcesses: HelpOperationsProcess[];
}

export interface HelpOperationsContext {
  observedAt: string;
  environments: HelpOperationsEnvironment[];
}

export interface HelpSource {
  id: string;
  title: string;
  route: string;
  excerpt: string;
}

export type HelpDiagnosticKind = 'CARD' | 'BATCH' | 'OPERATIONS';
export type HelpDiagnosticStatus = 'OK' | 'ATTENTION' | 'BLOCKED' | 'NOT_FOUND' | 'AMBIGUOUS';

export interface HelpDiagnosticEvidence {
  label: string;
  value: string;
}

export interface HelpDiagnosticLink {
  title: string;
  route: string;
}

export interface HelpDiagnosticResult {
  kind: HelpDiagnosticKind;
  targetRef: string;
  targetId?: string;
  displayId: string;
  status: HelpDiagnosticStatus;
  summary: string;
  blockers: string[];
  evidence: HelpDiagnosticEvidence[];
  links: HelpDiagnosticLink[];
  observedAt: string;
  truncated?: boolean;
}

export interface AskHelpResponse {
  interactionId: string;
  answer: string;
  sources: HelpSource[];
  grounded: boolean;
  generatedBy: 'BEDROCK' | 'DOCUMENTATION_FALLBACK' | 'NO_MATCH';
  contentVersion: string;
  diagnostic?: HelpDiagnosticResult;
}

export type HelpFeedbackSignal = 'USER_UNHELPFUL' | 'NO_MATCH' | 'DIAGNOSTIC_FAILURE';

export type HelpFeedbackReviewStatus =
  | 'NEW'
  | 'NEEDS_DOCUMENTATION'
  | 'NEEDS_DIAGNOSTIC'
  | 'ANSWER_INCORRECT'
  | 'RESOLVED'
  | 'IGNORED';

export interface HelpFeedbackSource {
  id: string;
  title: string;
}

export interface CreateHelpFeedbackRequest {
  interactionId: string;
  question: string;
  answer?: string;
  currentRoute?: string;
  generatedBy?: AskHelpResponse['generatedBy'];
  contentVersion?: string;
  sources?: HelpFeedbackSource[];
  note?: string;
}

export interface HelpFeedbackRecord {
  feedbackId: string;
  interactionId: string;
  signal: HelpFeedbackSignal;
  reviewStatus: HelpFeedbackReviewStatus;
  question: string;
  answer?: string;
  currentRoute?: string;
  generatedBy?: AskHelpResponse['generatedBy'];
  contentVersion: string;
  sources: HelpFeedbackSource[];
  diagnosticKind?: HelpDiagnosticKind;
  diagnosticStatus?: HelpDiagnosticStatus;
  note?: string;
  questionFingerprint: string;
  createdAt: string;
  updatedAt: string;
  expiresAt: number;
}

export interface CreateHelpFeedbackResponse {
  item: HelpFeedbackRecord;
}

export interface ListHelpFeedbackResponse {
  items: HelpFeedbackRecord[];
}

export interface UpdateHelpFeedbackRequest {
  reviewStatus: HelpFeedbackReviewStatus;
}

export interface UpdateHelpFeedbackResponse {
  item: HelpFeedbackRecord;
}
